import { InjectionToken } from '@angular/core';

/**
 * Unified logging interface for all PCP applications.
 *
 * Usage:
 * ```typescript
 * private readonly log = inject(PCP_LOGGER).createScoped('signalr');
 *
 * this.log.info('Connected', { connectionId });
 * this.log.error('Command failed', error, { command: 'SendChangeSlide' });
 * this.log.trackEvent('Reconnected', { retryCount: 3 });
 * ```
 *
 * @see .claude/skills/logging/references/logging-strategy.md
 */
export interface PcpLoggerInterface {
    verbose(message: string, properties?: Record<string, unknown>): void;
    debug(message: string, properties?: Record<string, unknown>): void;
    info(message: string, properties?: Record<string, unknown>): void;
    warn(message: string, properties?: Record<string, unknown>): void;
    error(message: string, error?: Error, properties?: Record<string, unknown>): void;
    critical(message: string, error?: Error, properties?: Record<string, unknown>): void;
    trackEvent(name: string, properties?: Record<string, unknown>): void;
    trackMetric(name: string, value: number, properties?: Record<string, unknown>): void;
    createScoped(domain: string): PcpLoggerInterface;
}

/**
 * Injection token for the unified PCP logger.
 *
 * Usage:
 * ```typescript
 * private readonly log = inject(PCP_LOGGER).createScoped('myDomain');
 * ```
 */
export const PCP_LOGGER = new InjectionToken<PcpLoggerInterface>('PCP_LOGGER');
